<template>
  <v-card class="mb-4 w-100">
    <v-card-title class="d-flex justify-end pa-4 pa-sm-6">
      <v-btn
        color="primary"
        variant="text"
        @click="addRow"
        prepend-icon="mdi-plus"
        data-testid="add-row-btn"
        size="small"
        class="text-body-2"
      >
        <span class="d-none d-sm-inline">Add Information Row</span>
        <span class="d-sm-none">Add Row</span>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <!-- Row Configuration -->
      <v-expansion-panels variant="accordion" class="w-100" v-model="openPanel">
        <draggable
          class="w-100"
          v-model="draggableRows"
          item-key="id"
          handle=".drag-handle"
          @end="onDragEnd"
          tag="div"
        >
          <template #item="{ element: row, index }">
            <v-expansion-panel
              class="w-100 mb-2"
              :value="index"
              :data-testid="`vehicle-row-${index}`"
            >
              <v-expansion-panel-title
                class="d-flex flex-column flex-sm-row align-start align-sm-center pa-3 pa-sm-4"
              >
                <div
                  class="d-flex align-center flex-grow-1 min-width-0 mb-2 mb-sm-0"
                >
                  <!-- Drag Handle -->
                  <v-icon
                    class="drag-handle mr-2"
                    size="small"
                    color="grey-darken-1"
                    style="cursor: grab"
                  >
                    mdi-drag-vertical
                  </v-icon>
                  <v-icon class="mr-3" :icon="getRowIcon(row)" size="small" />
                  <span class="font-weight-medium text-wrap">{{
                    getRowTitle(row)
                  }}</span>
                  <v-chip
                    v-if="row.fieldType === 'conditional'"
                    size="x-small"
                    color="warning"
                    class="ml-2 d-none d-sm-flex align-center"
                  >
                    Conditional
                  </v-chip>
                </div>
                <div class="d-flex align-center gap-1">
                  <!-- Visibility Toggle -->
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon
                        size="small"
                        variant="text"
                        @click.stop="updateRowVisibility(index)"
                        :data-testid="`visibility-toggle-${index}`"
                        class="touch-target"
                      >
                        <v-icon
                          :color="
                            row.visible !== false ? 'primary' : 'grey-darken-1'
                          "
                        >
                          {{
                            row.visible !== false ? 'mdi-eye' : 'mdi-eye-off'
                          }}
                        </v-icon>
                      </v-btn>
                    </template>
                    {{ row.visible !== false ? 'Hide row' : 'Show row' }}
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-delete"
                        variant="text"
                        size="small"
                        color="error"
                        @click.stop="confirmDelete(index)"
                        :data-testid="`delete-btn-${index}`"
                        class="touch-target"
                      />
                    </template>
                    Remove row
                  </v-tooltip>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <!-- Field Selection -->
                    <v-card variant="outlined" class="mb-3">
                      <v-card-title class="text-subtitle-2"
                        >Information Type</v-card-title
                      >
                      <v-card-text>
                        <v-select
                          v-model="row.fieldType"
                          :items="fieldTypeOptions"
                          label="Select information to display"
                          @update:model-value="updateRowField(index, $event)"
                        />

                        <!-- Field-specific options -->
                        <div v-if="row.fieldType === 'basic'">
                          <v-select
                            v-model="row.fields"
                            :items="basicFieldOptions"
                            label="Vehicle details"
                            multiple
                            chips
                            closable-chips
                            @update:model-value="updateRowFields(index, $event)"
                          />
                        </div>

                        <div v-else-if="row.fieldType === 'price'">
                          <v-select
                            v-model="row.priceField"
                            :items="priceFieldOptions"
                            label="Price type"
                            @update:model-value="
                              updateRowPriceField(index, $event)
                            "
                          />

                          <!-- Custom pricing field input -->
                          <v-text-field
                            v-if="row.priceField === 'custom'"
                            v-model="row.customPriceField"
                            label="Custom pricing field"
                            hint="Enter field name (e.g., @Price, @MSRP, @Discount)"
                            persistent-hint
                            placeholder="@Price"
                            @update:model-value="
                              updateRowCustomPriceField(index, $event)
                            "
                          />

                          <!-- Custom label input -->
                          <v-text-field
                            v-model="row.priceLabel"
                            label="Custom label (optional)"
                            hint="Text to display before the price (e.g., 'Starting at:', 'Special Price:')"
                            persistent-hint
                            placeholder="Price:"
                            @update:model-value="
                              updateRowPriceLabel(index, $event)
                            "
                          />
                        </div>

                        <div v-else-if="row.fieldType === 'conditional'">
                          <v-select
                            v-model="row.conditionField"
                            :items="conditionFieldOptions"
                            label="Condition field"
                            @update:model-value="
                              updateRowConditionField(index, $event)
                            "
                          />

                          <!-- Custom field name input (only show for custom pricing) -->
                          <v-text-field
                            v-if="row.conditionField === 'custom'"
                            v-model="row.customConditionField"
                            label="Custom field name"
                            hint="Enter the custom pricing field name (e.g., '@Price', '@MSRP', '@Discount')"
                            persistent-hint
                            placeholder="@Price"
                            @update:model-value="
                              updateRowCustomConditionField(index, $event)
                            "
                          />

                          <!-- Show Value (when condition is true) -->
                          <v-card variant="outlined" class="mb-3">
                            <v-card-title class="text-subtitle-2"
                              >Display Format (when value is
                              available)</v-card-title
                            >
                            <v-card-text>
                              <v-text-field
                                v-model="row.conditionPrefix"
                                label="Prefix (optional)"
                                hint="Text to show before the field value (e.g., 'Mileage:', 'Price:')"
                                persistent-hint
                                placeholder=""
                                @update:model-value="
                                  updateRowConditionPrefix(index, $event)
                                "
                              />

                              <v-text-field
                                v-model="row.conditionSuffix"
                                label="Suffix (optional)"
                                hint="Text to show after the field value (e.g., 'miles', '(negotiable)')"
                                persistent-hint
                                placeholder=""
                                @update:model-value="
                                  updateRowConditionSuffix(index, $event)
                                "
                              />
                            </v-card-text>
                          </v-card>

                          <!-- Hide Value (when condition is false) -->
                          <v-text-field
                            v-model="row.hideValue"
                            label="Display Text (when value is not available)"
                            hint="Text to show when the value is empty or zero (e.g., 'Call for pricing', 'Not available')"
                            persistent-hint
                            placeholder=""
                            @update:model-value="
                              updateRowHideValue(index, $event)
                            "
                          />
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <!-- Styling -->
                    <v-card variant="outlined">
                      <v-card-title class="text-subtitle-2"
                        >Styling</v-card-title
                      >
                      <v-card-text>
                        <v-tabs v-model="activeTab" density="compact">
                          <v-tab value="colors">Colors</v-tab>
                          <v-tab value="typography">Typography</v-tab>
                        </v-tabs>

                        <v-window v-model="activeTab" class="mt-3">
                          <v-window-item value="colors">
                            <ColorSettings
                              :model-value="row.colors"
                              :excluded="['background', 'backgroundHover']"
                              @update:model-value="
                                updateRow(index, 'colors', $event)
                              "
                            />
                          </v-window-item>

                          <v-window-item value="typography">
                            <TypographySettings
                              :excluded="[]"
                              :model-value="row.typography"
                              :googleFonts="{
                                fonts: ['Roboto', 'Open Sans', 'Lato'],
                                display: 'swap',
                                disabled: false,
                              }"
                              @update:model-value="
                                updateRow(index, 'typography', $event)
                              "
                            />
                          </v-window-item>
                        </v-window>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </template>
        </draggable>
      </v-expansion-panels>
    </v-card-text>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Remove Information Row</v-card-title>
        <v-card-text>
          Are you sure you want to remove this information row from the vehicle
          cards?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="showDeleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn color="error" variant="text" @click="handleDelete">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { IVehicleDetailRow } from '@/types'
import { ColorSettings, TypographySettings } from '@jazel/jazel-vuetify'
import {
  getDefaultVehicleFilterRowColors,
  getDefaultVehicleFilterRowTypography,
} from '@/stores/utils'
import draggable from 'vuedraggable'

const activeTab = ref('colors')
const showDeleteDialog = ref(false)
const pendingDeleteIndex = ref<number | null>(null)
const openPanel = ref<number | undefined>(undefined)

const props = defineProps<{
  modelValue: IVehicleDetailRow[]
}>()

const emit = defineEmits<{
  'update:model-value': [value: IVehicleDetailRow[]]
}>()

// Draggable computed property with unique IDs
const draggableRows = computed({
  get: () => props.modelValue.map((row, index) => ({ ...row, id: index })),
  set: newValue => {
    const reorderedRows = newValue.map(({ id, ...row }) => row)
    emit('update:model-value', reorderedRows)
  },
})

// Drag end handler
const onDragEnd = () => {
  // The draggableRows computed setter handles the reordering
  // No additional logic needed here
}

// Field type options
const fieldTypeOptions = [
  { title: 'Basic Vehicle Info', value: 'basic', icon: 'mdi-car' },
  { title: 'Price Information', value: 'price', icon: 'mdi-currency-usd' },
  {
    title: 'Conditional Display',
    value: 'conditional',
    icon: 'mdi-help-circle',
  },
]

// Basic field options
const basicFieldOptions = [
  { title: 'Year', value: 'year' },
  { title: 'Make', value: 'make' },
  { title: 'Model', value: 'model' },
  { title: 'Trim', value: 'trim' },
  { title: 'Style', value: 'style' },
  { title: 'Stock Number', value: 'stockNumber' },
  { title: 'VIN', value: 'vin' },
  { title: 'Mileage', value: 'mileage' },
  { title: 'Conditions', value: 'conditions' },
  { title: 'Exterior Color', value: 'exteriorColor' },
  { title: 'Interior Color', value: 'interiorColor' },
]

// Price field options
const priceFieldOptions = [
  { title: 'Sale Price', value: 'price' },
  { title: 'MSRP', value: 'msrp' },
  { title: 'Custom Pricing Field', value: 'custom' },
]

// Condition field options
const conditionFieldOptions = [
  { title: 'Price Available', value: 'price' },
  { title: 'MSRP Available', value: 'msrp' },
  { title: 'Mileage Available', value: 'mileage' },
  { title: 'Custom Pricing Field', value: 'custom' },
]

const conditionValueOptions = (field: string | undefined) => {
  // For price, msrp, mileage - no condition value needed, just check if available
  return []
}

// Helper functions with safe property access
const getRowIcon = (row: IVehicleDetailRow) => {
  const fieldType = row.fieldType || inferFieldType(row.template)
  const option = fieldTypeOptions.find(opt => opt.value === fieldType)
  return option?.icon || 'mdi-text'
}

const getRowTitle = (row: IVehicleDetailRow) => {
  const fieldType = row.fieldType || inferFieldType(row.template)

  if (fieldType === 'basic') {
    const fields = row.fields || inferBasicFields(row.template)
    return fields.length > 0
      ? fields
          .map((f: string) => f.charAt(0).toUpperCase() + f.slice(1))
          .join(' ')
      : 'Basic Info'
  } else if (fieldType === 'price') {
    const priceField = row.priceField || inferPriceField(row.template)
    const option = priceFieldOptions.find(opt => opt.value === priceField)
    return option?.title || 'Price'
  } else if (fieldType === 'conditional') {
    const conditionField =
      row.conditionField || inferConditionField(row.template)
    if (conditionField === 'price') {
      return 'Price Available'
    } else if (conditionField === 'msrp') {
      return 'MSRP Available'
    } else if (conditionField === 'mileage') {
      return 'Mileage Available'
    } else if (conditionField === 'custom') {
      return 'Custom Pricing'
    }
    return 'Conditional Display'
  }
  return 'Information Row'
}

const getPreviewText = (row: IVehicleDetailRow) => {
  const fieldType = row.fieldType || inferFieldType(row.template)

  if (fieldType === 'basic') {
    const fields = row.fields || inferBasicFields(row.template)
    return fields.length > 0
      ? fields.map((f: string) => `{{${f}}}`).join(' ')
      : 'Vehicle Information'
  } else if (fieldType === 'price') {
    const priceField = row.priceField || inferPriceField(row.template)
    const priceLabel = row.priceLabel || ''

    if (priceField === 'price') return `${priceLabel}{{price}}`
    if (priceField === 'msrp') return `${priceLabel}{{msrp}}`
    if (priceField === 'custom') {
      const customField = row.customPriceField || '@Price'
      return `${priceLabel}{{${customField}}}`
    }
    return `${priceLabel}{{price}}`
  } else if (fieldType === 'conditional') {
    const conditionField =
      row.conditionField || inferConditionField(row.template)
    const conditionValue = row.conditionValue || '1'
    const prefix = row.conditionPrefix || ''
    const suffix = row.conditionSuffix || ''
    const hideValue = row.hideValue || ''

    if (conditionField === 'price') {
      return `{{price|conditional|${prefix}|${suffix}|${hideValue}}}`
    } else if (conditionField === 'msrp') {
      return `{{msrp|conditional|${prefix}|${suffix}|${hideValue}}}`
    } else if (conditionField === 'mileage') {
      return `{{mileage|conditional|${prefix}|${suffix}|${hideValue}}}`
    } else if (conditionField === 'custom') {
      const customFieldName = row.customConditionField || '@Price'
      return `{{${customFieldName}|conditional|${prefix}|${suffix}|${hideValue}}}`
    }
    return 'Conditional Text'
  }
  return 'Preview Text'
}

const getPreviewStyles = (row: IVehicleDetailRow) => {
  return {
    color: row.colors?.color || '#000000',
    fontSize: `${row.typography?.fontSize || 16}${row.typography?.fontSizeUnit || 'px'}`,
    fontWeight: row.typography?.fontWeight || 'normal',
    fontFamily: row.typography?.fontFamily || 'inherit',
  }
}

// Template inference functions for backward compatibility
const inferFieldType = (
  template: string,
): 'basic' | 'price' | 'conditional' => {
  if (template.includes('{{price}}') || template.includes('{{msrp}}')) {
    return 'price'
  }
  if (template.includes('?') && template.includes(':')) {
    return 'conditional'
  }
  return 'basic'
}

const inferBasicFields = (template: string): string[] => {
  const matches = template.match(/\{\{(\w+)\}\}/g)
  if (!matches) return []
  return matches.map(match => match.replace(/[{}]/g, ''))
}

const inferPriceField = (
  template: string,
): 'price' | 'msrp' | 'both' | 'custom' => {
  if (template.includes('{{price}}') && template.includes('{{msrp}}')) {
    return 'both'
  }
  if (template.includes('{{msrp}}')) {
    return 'msrp'
  }
  if (template.includes('{{@')) {
    return 'custom'
  }
  return 'price'
}

const inferCustomPriceField = (template: string): string => {
  const match = template.match(/\{\{(@\w+)\}\}/)
  return match ? match[1] : '@Price'
}

const inferPriceLabel = (template: string): string => {
  // Look for text before {{price}}, {{msrp}}, or {{@...}}
  const match = template.match(/^([^{]*?)(\{\{.*\}\})/)
  return match ? match[1].trim() : ''
}

const inferConditionField = (
  template: string,
): 'msrp' | 'mileage' | 'price' | 'custom' => {
  if (template.includes('{{price}}')) {
    return 'price'
  }
  if (template.includes('{{msrp}}')) {
    return 'msrp'
  }
  if (template.includes('{{mileage}}')) {
    return 'mileage'
  }
  if (template.includes('{{@')) {
    return 'custom'
  }
  return 'price'
}

const inferConditionPrefix = (template: string): string => {
  // Look for text before the conditional expression
  const match = template.match(/^([^{]*?)(\{\{.*\}\})/)
  return match ? match[1].trim() : ''
}

const inferConditionSuffix = (template: string): string => {
  // Look for text after the conditional expression
  const match = template.match(/\{\{.*\}\}([^{]*?)$/)
  return match ? match[1].trim() : ''
}

const inferHideValue = (template: string): string => {
  // Look for the false part of the conditional expression
  const match = template.match(/\{\{.*\?\s*"[^"]*"\s*:\s*"([^"]*)"\}\}/)
  return match ? match[1] : ''
}

// Update functions
const updateRowField = (index: number, fieldType: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index] = {
    ...updatedRows[index],
    fieldType: fieldType as 'basic' | 'price' | 'conditional',
    fields: fieldType === 'basic' ? ['year', 'make', 'model'] : undefined,
    priceField: fieldType === 'price' ? 'price' : undefined,
    conditionField: fieldType === 'conditional' ? 'price' : undefined,
    conditionValue: fieldType === 'conditional' ? undefined : undefined,
    trueText: fieldType === 'conditional' ? undefined : undefined,
    falseText: fieldType === 'conditional' ? undefined : undefined,
  }

  // Generate template based on new field type
  updatedRows[index].template = generateTemplate(updatedRows[index])

  // Validate the generated template
  if (!validateTemplate(updatedRows[index].template)) {
    console.warn(
      'Generated template validation failed:',
      updatedRows[index].template,
    )
  }

  emit('update:model-value', updatedRows)
}

const updateRowFields = (index: number, fields: string[]) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].fields = fields
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowPriceField = (index: number, priceField: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].priceField = priceField as 'price' | 'msrp' | 'custom'

  // Set default custom field if switching to custom
  if (priceField === 'custom' && !updatedRows[index].customPriceField) {
    updatedRows[index].customPriceField = '@Price'
  }

  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowCustomPriceField = (index: number, customField: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].customPriceField = customField
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowPriceLabel = (index: number, priceLabel: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].priceLabel = priceLabel
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowConditionField = (index: number, conditionField: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].conditionField = conditionField as
    | 'msrp'
    | 'mileage'
    | 'price'
    | 'custom'
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowCustomConditionField = (index: number, customField: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].customConditionField = customField
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowConditionValue = (index: number, conditionValue: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].conditionValue = conditionValue
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowTrueText = (index: number, trueText: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].trueText = trueText
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowFalseText = (index: number, falseText: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].falseText = falseText
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowConditionPrefix = (index: number, prefix: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].conditionPrefix = prefix
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowConditionSuffix = (index: number, suffix: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].conditionSuffix = suffix
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const updateRowHideValue = (index: number, hideValue: string) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index].hideValue = hideValue
  updatedRows[index].template = generateTemplate(updatedRows[index])
  emit('update:model-value', updatedRows)
}

const generateTemplate = (row: IVehicleDetailRow): string => {
  const fieldType = row.fieldType || inferFieldType(row.template)

  if (fieldType === 'basic') {
    const fields = row.fields || inferBasicFields(row.template)
    return fields.map((f: string) => `{{${f}}}`).join(' ')
  } else if (fieldType === 'price') {
    const priceField = row.priceField || inferPriceField(row.template)
    const priceLabel = row.priceLabel || ''

    if (priceField === 'price') return `${priceLabel}{{price}}`
    if (priceField === 'msrp')
      return `${priceLabel}{{msrp|conditional|||${priceLabel}}}`
    if (priceField === 'custom') {
      const customField =
        row.customPriceField || inferCustomPriceField(row.template)
      return `${priceLabel}{{${customField}|conditional|||${priceLabel}}}`
    }
    return `${priceLabel}{{price}}`
  } else if (fieldType === 'conditional') {
    const conditionField =
      row.conditionField || inferConditionField(row.template)
    const prefix = row.conditionPrefix || ''
    const suffix = row.conditionSuffix || ''
    const hideValue = row.hideValue || ''

    if (conditionField === 'price') {
      return `{{price|conditional|${prefix}|${suffix}|${hideValue}}}`
    } else if (conditionField === 'msrp') {
      return `{{msrp|conditional|${prefix}|${suffix}|${hideValue}}}`
    } else if (conditionField === 'mileage') {
      return `{{mileage|conditional|${prefix}|${suffix}|${hideValue}}}`
    } else if (conditionField === 'custom') {
      const customFieldName = row.customConditionField || '@Price'
      return `{{${customFieldName}|conditional|${prefix}|${suffix}|${hideValue}}}`
    }
    return '{{conditions}}'
  }
  return ''
}

const addRow = () => {
  const newRow: IVehicleDetailRow = {
    template: '{{year}} {{make}} {{model}}',
    fieldType: 'basic',
    fields: ['year', 'make', 'model'],
    visible: true,
    colors: getDefaultVehicleFilterRowColors(),
    typography: getDefaultVehicleFilterRowTypography(),
  }

  emit('update:model-value', [...props.modelValue, newRow])
}

const confirmDelete = (index: number) => {
  pendingDeleteIndex.value = index
  showDeleteDialog.value = true
}

const handleDelete = () => {
  if (pendingDeleteIndex.value !== null) {
    const updatedRows = [...props.modelValue]
    updatedRows.splice(pendingDeleteIndex.value, 1)
    emit('update:model-value', updatedRows)
  }
  showDeleteDialog.value = false
  pendingDeleteIndex.value = null
}

const updateRowVisibility = (index: number) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index] = {
    ...updatedRows[index],
    visible: !updatedRows[index].visible,
  }
  emit('update:model-value', updatedRows)
}

const updateRow = (index: number, key: keyof IVehicleDetailRow, value: any) => {
  const updatedRows = [...props.modelValue]
  updatedRows[index] = {
    ...updatedRows[index],
    [key]: value,
  }
  emit('update:model-value', updatedRows)
}

const validateTemplate = (template: string): boolean => {
  // Check if conditional templates have the correct format
  if (template.includes('|conditional|')) {
    const parts = template.split('|conditional|')
    if (parts.length !== 2) return false

    const [fieldPart, conditionalPart] = parts
    const conditionalParts = conditionalPart.split('|')
    if (conditionalParts.length !== 3) return false

    // Check if field part is properly formatted
    if (!fieldPart.match(/^\{\{[^}]+\}\}$/)) return false

    return true
  }

  // Check if basic templates have proper field references
  if (template.includes('{{')) {
    const fieldMatches = template.match(/\{\{[^}]+\}\}/g)
    if (!fieldMatches) return false

    for (const match of fieldMatches) {
      if (!match.match(/^\{\{[^}]+\}\}$/)) return false
    }
  }

  return true
}
</script>

<style scoped>
.layout-preview {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.preview-card {
  background: white;
  max-width: 300px;
}

.preview-row {
  min-height: 20px;
  line-height: 1.4;
}

.drag-handle {
  cursor: grab;
  transition: color 0.2s ease;
}

.drag-handle:hover {
  color: var(--v-primary-base) !important;
}

.drag-handle:active {
  cursor: grabbing;
}

/* Dragging state styles */
.sortable-ghost {
  opacity: 0.5;
}

.sortable-chosen {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Mobile-friendly styles */
.text-wrap {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.touch-target {
  min-height: 44px;
  min-width: 44px;
}

/* Ensure proper spacing on mobile */
@media (max-width: 599px) {
  .v-expansion-panel-title {
    min-height: auto;
  }

  .v-expansion-panel-title__overlay {
    display: none;
  }
}

/* Responsive text sizing */
@media (max-width: 599px) {
  .text-h6 {
    font-size: 1.125rem !important;
    line-height: 1.4 !important;
  }

  .text-body-2 {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
  }
}
</style>
